import React from 'react';
import { Formik, ErrorMessage } from 'formik';
import clone from 'lodash/clone';
import isArray from 'lodash/isArray';
import { CSSTransition } from 'react-transition-group';
import './styles.css';
import BasicButtonGroup from '../../../../components/BasicButtonGroup';
import BasicLabel from '../../../../components/BasicLabel';
import BasicSelect from '../../../../components/BasicSelect';
import BasicSubmitButton from '../../../../components/BasicSubmitButton';
import BasicTextArea from '../../../../components/BasicTextArea';
import CheckboxMultiSelect from '../../../../components/CheckboxMultiSelect';
import HorizontalLoader from '../../../../components/HorizontalLoader';
import LoadMask from '../../../../components/LoadMask';
import {ANIMATION_TIMEOUT_APPEAR} from '../../../../constants/animation';
import ENDPOINT from '../../../../constants/endpoints';
import {get, post} from '../../../../services/api';
import {AddRecommendationFormSchema} from './validate';

class AddRecommendationForm extends React.Component {
  _isMounted = false;

  constructor(props) {
    super(props);
    this.state = {
      init: false,
      fields: {
        token: null,
        recommendation: '',
        q1: null,
        q2: null,
        q3: null,
        q4: null,
        q5: null,
      },
      schema: {},
    };
  }

  componentDidMount() {
    this._isMounted = true;

    get(
      ENDPOINT.FORM.GET.RECOMMENDATION,
      { token: this.props.params.token },
      {
        handleSuccess: formData => {
          if (this._isMounted) {
            this.setState({
              init: true,
              schema: {
                ...formData.schema,
              },
              fields: {
                ...this.state.fields,
                token: formData.schema.token.value,
              },
            });
          }
        },
        handleError: error => this.props.actions.onNotFoundHandler(error),
      }
    );
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  handleSubmit = (values, { setSubmitting, setErrors }) => {
    const preparedValues = clone(values);

    Object.keys(preparedValues).forEach(field => {
      let data = preparedValues[field];
      if (data === null) {
        delete preparedValues[field];
      } else if (isArray(data)) {
        preparedValues[field] = data.map(item => item.id);
      } else if (typeof data === 'object') {
        preparedValues[field] = data.value;
      }
    });

    post(ENDPOINT.FORM.SAVE.RECOMMENDATION, preparedValues, {
      handleError: response =>
        this._isMounted
          ? this.props.actions.onErrorHandler(response, setErrors)
          : null,
      handleSuccess: () => this.props.actions.proceed(),
      handleComplete: () => (this._isMounted ? setSubmitting(false) : null),
    });
  };

  render() {
    if (!this.state.init) {
      return (
        <div className="form__inner">
          <div className="horizontal-loader__container">
            <HorizontalLoader />
          </div>
        </div>
      );
    }

    const {
      fields,
      schema: { recommendation, q1, q2, q3, q4, q5 },
    } = this.state;

    return (
      <CSSTransition
        in={true}
        appear={true}
        timeout={ANIMATION_TIMEOUT_APPEAR}
        classNames="fade"
      >
        <div className="form__inner">
          <div className="form__header">
            <h4 className="form__title">Add Recommendation</h4>
          </div>
          <Formik
            initialValues={fields}
            validationSchema={AddRecommendationFormSchema}
            onSubmit={this.handleSubmit}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              setFieldValue,
              setFieldTouched,
            }) => (
              <form onSubmit={handleSubmit} className="form__content">
                {isSubmitting && <LoadMask />}

                <div className="form__group">
                  <BasicLabel
                    name="recommendation"
                    required={recommendation.required}
                    text={recommendation.label}
                    styles={{
                      fontWeight: 'bold',
                    }}
                  />
                  <BasicTextArea
                    rows={10}
                    name="recommendation"
                    content={values.recommendation}
                    resize={false}
                    placeholder={`0 characters of the ${recommendation.limit} character limit`}
                    controlFunc={handleChange}
                    touchFunc={handleBlur}
                    isValid={
                      (!errors.recommendation && !!touched.recommendation) ||
                      !touched.recommendation
                    }
                  />

                  <ErrorMessage
                    name="recommendation"
                    render={message => (
                      <div className="form__error">{message}</div>
                    )}
                  />
                </div>

                <div className="form__group">
                  <BasicLabel
                    name="questions"
                    required={false}
                    text="Please answer the questions below"
                    styles={{
                      fontWeight: 'bold',
                      marginTop: '25px',
                    }}
                  />
                  <p className="form__description">
                    The following questions are on a 1 to 5 scale with 1 being
                    well below average and 5 being well above average. All
                    responses to the below are kept confidential.
                  </p>
                </div>

                <div className="form__group" style={{ marginTop: '15px' }}>
                  <BasicLabel
                    name="q1"
                    required={q1.required}
                    text={q1.label}
                  />
                  <BasicSelect
                    name="q1"
                    value={values.q1}
                    isValid={(!errors.q1 && !!touched.q1) || !touched.q1}
                    placeholder="Select From List"
                    options={q1.options}
                    onChange={setFieldValue}
                    onBlur={setFieldTouched}
                  />

                  <ErrorMessage
                    name="q1"
                    render={message => (
                      <div className="form__error">{message}</div>
                    )}
                  />
                </div>

                <div className="form__group">
                  <BasicLabel
                    name="q2"
                    required={q2.required}
                    text={q2.label}
                  />
                  <BasicSelect
                    name="q2"
                    value={values.q2}
                    isValid={(!errors.q2 && !!touched.q2) || !touched.q2}
                    placeholder="Select From List"
                    options={q2.options}
                    onChange={setFieldValue}
                    onBlur={setFieldTouched}
                  />

                  <ErrorMessage
                    name="q2"
                    render={message => (
                      <div className="form__error">{message}</div>
                    )}
                  />
                </div>

                <div className="form__group">
                  <BasicLabel
                    name="q3"
                    required={q3.required}
                    text={q3.label}
                  />
                  <BasicSelect
                    name="q3"
                    value={values.q3}
                    isValid={(!errors.q3 && !!touched.q3) || !touched.q3}
                    placeholder="Select From List"
                    options={q3.options}
                    onChange={setFieldValue}
                    onBlur={setFieldTouched}
                  />

                  <ErrorMessage
                    name="q3"
                    render={message => (
                      <div className="form__error">{message}</div>
                    )}
                  />
                </div>

                <div className="form__group">
                  <BasicLabel
                    name="q4"
                    required={q4.required}
                    text={q4.label}
                  />
                  <BasicSelect
                    name="q4"
                    value={values.q4}
                    isValid={(!errors.q4 && !!touched.q4) || !touched.q4}
                    placeholder="Select From List"
                    options={q4.options}
                    onChange={setFieldValue}
                    onBlur={setFieldTouched}
                  />

                  <ErrorMessage
                    name="q4"
                    render={message => (
                      <div className="form__error">{message}</div>
                    )}
                  />
                </div>

                <div className="form__group" style={{ marginBottom: '30px' }}>
                  <CheckboxMultiSelect
                    name="q5"
                    label={q5.label}
                    required={q5.required}
                    isValid={(!errors.q5 && !!touched.q5) || !touched.q5}
                    selectedItems={values.q5}
                    items={q5.options.map(item => ({
                      ...item,
                      id: item.value,
                    }))}
                    placeholder=""
                    groups={false}
                    onChange={setFieldValue}
                    onBlur={setFieldTouched}
                  />
                  <ErrorMessage
                    name="q5"
                    render={message => (
                      <div className="form__error">{message}</div>
                    )}
                  />
                </div>

                <div className="form__controls">
                  <BasicButtonGroup>
                    <BasicSubmitButton
                      controlFunc={handleSubmit}
                      btnWidth={200}
                      btnHeight={40}
                      btnFontWeight={500}
                      btnArrow={true}
                      btnTextColor="#27282d"
                      btnText="Save"
                    />
                  </BasicButtonGroup>
                </div>
              </form>
            )}
          </Formik>
        </div>
      </CSSTransition>
    );
  }
}

export default AddRecommendationForm;
